import { type ReactNode, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

// #35 / Naht für #37: bewusst inhaltsfrei. Das Panel kennt nur Titel, Kinder und onClose —
// dadurch kann das Defect-Board (#37) es unverändert wiederverwenden, statt ein zweites Overlay
// mit eigenem Escape-/Fokus-Verhalten zu bauen.
export function SidePanel({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const panelRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    // Fokus ins Panel holen, sonst bleibt er auf der Tabellenzeile dahinter liegen und Tab
    // führt hinter das Overlay statt hinein.
    panelRef.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <button
        type="button"
        aria-label={t('common.close')}
        onClick={onClose}
        className="absolute inset-0 bg-black/30"
      />
      {/* Natives <dialog open> statt div[role=dialog]: die Rolle kommt implizit. Bewusst `open`
          und nicht showModal() — showModal() hebt das Element in den Top-Layer, wodurch unser
          eigener Backdrop-Button darunter läge und nicht mehr klickbar wäre. Escape behandeln
          wir ohnehin selbst (useEffect oben). m-0/p-0 setzt die UA-Standardstile zurück. */}
      <dialog
        ref={panelRef}
        open
        tabIndex={-1}
        aria-modal="true"
        aria-label={title}
        className="relative z-10 m-0 flex h-full w-full max-w-xl flex-col overflow-y-auto border-l border-[var(--border)] bg-bg-surface p-0 text-text-body shadow-xl outline-none"
      >
        <div className="flex items-center justify-between border-b border-[var(--border)] px-4 py-3">
          <h2 className="text-sm font-semibold text-text-primary">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className="rounded-btn px-2 py-1 text-text-muted hover:text-text-primary"
          >
            ✕
          </button>
        </div>
        <div className="flex-1 space-y-3 p-4">{children}</div>
      </dialog>
    </div>
  );
}
